import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { EmailRecipient } from "@/components/crm/messages/ClientEmailSelector";
import {
  hasTemplateVariables,
  mapContactToTemplateVariables,
  replaceTemplateVariables,
} from "@/lib/utils/crm/template-variables";
import type {
  ComposeFormValues,
  ComposePersonalizationOrigin,
} from "@/lib/types/compose-modal-types";
import type { Client } from "@/lib/types/database";
import {
  composeLoneToRecipientIdentityKey,
  contactRowMatchesComposeRecipientEmail,
  resolveComposeClientFromSingleToRecipient,
} from "./recipient-helpers";

/**
 * A compose client seeded from a modal item (deriveInitialClient) can be a
 * trimmed synthetic that carries id/name/email but no company data. When the
 * lone-recipient auto-match later resolves the SAME contact from a fully-loaded
 * source (clientOptions / useContactByEmail, both of which now embed companies),
 * we must adopt that fuller row so {{company_name}} & co. can still fill.
 * Returns true when `candidate` has company data the current selection lacks.
 */
export function candidateEnrichesSelectedClient(
  current: Client | null,
  candidate: Client
): boolean {
  if (!current) return true;
  if (current.id !== candidate.id) return false;
  const currentHasCompany = Boolean(current.company_name?.trim() || current.companies?.name);
  const candidateHasCompany = Boolean(
    candidate.company_name?.trim() || candidate.companies?.name
  );
  return !currentHasCompany && candidateHasCompany;
}

interface UseComposePersonalizationParams {
  isOpen: boolean;
  initialClient?: Client;
  form: UseFormReturn<ComposeFormValues>;
  recipients: EmailRecipient[];
  clientOptions: Client[];
  existingContact?: unknown;
  selectedClient: Client | null;
  setSelectedClient: (client: Client | null) => void;
}

export function useComposePersonalization({
  isOpen,
  initialClient,
  form,
  recipients,
  clientOptions,
  existingContact,
  selectedClient,
  setSelectedClient,
}: UseComposePersonalizationParams) {
  const [personalizationOrigin, setPersonalizationOrigin] =
    useState<ComposePersonalizationOrigin>(null);
  const autoSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastLoneToRecipientKeyRef = useRef<string | null>(null);

  const applyComposePersonalizationContact = useCallback(
    (contact: Client | null, origin: ComposePersonalizationOrigin = null) => {
      setSelectedClient(contact);
      setPersonalizationOrigin(contact ? origin : null);

      if (!contact) return;

      const currentTo = form.getValues("to");
      if (!currentTo && contact.email) {
        form.setValue("to", contact.email);
      }

      const currentSubject = form.getValues("subject");
      const currentBody = form.getValues("body");

      if (hasTemplateVariables(currentSubject) || hasTemplateVariables(currentBody)) {
        const variables = mapContactToTemplateVariables(contact);

        if (hasTemplateVariables(currentSubject)) {
          form.setValue("subject", replaceTemplateVariables(currentSubject, variables, false));
        }

        if (hasTemplateVariables(currentBody)) {
          form.setValue("body", replaceTemplateVariables(currentBody, variables, true));
        }
      }
    },
    [form, setSelectedClient]
  );

  const handleClientSelect = useCallback(
    (clientId: string) => {
      if (!clientId) {
        applyComposePersonalizationContact(null);
        return;
      }
      const selected = clientOptions.find((client) => client.id === clientId) ?? null;
      applyComposePersonalizationContact(selected, selected ? "manual_selection" : null);
    },
    [clientOptions, applyComposePersonalizationContact]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!initialClient) return;
    if (personalizationOrigin !== null) return;
    if (selectedClient?.id !== initialClient.id) return;
    setPersonalizationOrigin("opened_with_contact");
  }, [isOpen, initialClient?.id, selectedClient?.id, personalizationOrigin]);

  useEffect(() => {
    if (!isOpen) {
      lastLoneToRecipientKeyRef.current = null;
      setPersonalizationOrigin(null);
      return;
    }
    if (recipients.length !== 1) {
      lastLoneToRecipientKeyRef.current = null;
      return;
    }

    const recipient = recipients[0];
    if (!recipient.email?.trim()) return;

    const currentKey = composeLoneToRecipientIdentityKey(recipient);
    const loneRecipientChanged = lastLoneToRecipientKeyRef.current !== currentKey;
    let contact = resolveComposeClientFromSingleToRecipient(recipient, clientOptions);

    if (
      !contact &&
      recipient.type === "email" &&
      existingContact &&
      contactRowMatchesComposeRecipientEmail(
        existingContact,
        recipient.email
      )
    ) {
      contact = existingContact as Client;
    }

    if (!contact) {
      lastLoneToRecipientKeyRef.current = currentKey;
      return;
    }

    if (selectedClient?.id === contact.id) {
      // Same contact already selected — but the current selection may be a
      // trimmed synthetic (opened from a modal item) missing company data that
      // this fully-loaded row carries. Upgrade so {{company_name}} etc. fill.
      if (candidateEnrichesSelectedClient(selectedClient, contact)) {
        applyComposePersonalizationContact(contact, personalizationOrigin ?? "auto_from_to");
      }
      lastLoneToRecipientKeyRef.current = currentKey;
      return;
    }

    if (!selectedClient || loneRecipientChanged) {
      applyComposePersonalizationContact(contact, "auto_from_to");
      lastLoneToRecipientKeyRef.current = currentKey;
    }
  }, [
    isOpen,
    recipients,
    clientOptions,
    existingContact,
    selectedClient,
    personalizationOrigin,
    applyComposePersonalizationContact,
  ]);

  const handleVariableUpdate = useCallback(
    (variables: Record<string, string>) => {
      if (autoSyncTimeoutRef.current) {
        clearTimeout(autoSyncTimeoutRef.current);
      }

      autoSyncTimeoutRef.current = setTimeout(() => {
        const currentSubject = form.getValues("subject");
        const currentBody = form.getValues("body");

        if (hasTemplateVariables(currentSubject) || hasTemplateVariables(currentBody)) {
          let updatedSubject = currentSubject;
          let updatedBody = currentBody;

          Object.entries(variables).forEach(([key, value]) => {
            if (value && value !== "") {
              const regex = new RegExp(`\\{\\{${key}\\}\\}`, "g");
              updatedSubject = updatedSubject.replace(regex, value);
              updatedBody = updatedBody.replace(regex, value);
            }
          });

          if (updatedSubject !== currentSubject) {
            form.setValue("subject", updatedSubject);
          }
          if (updatedBody !== currentBody) {
            form.setValue("body", updatedBody);
          }
        }
      }, 1000);
    },
    [form]
  );

  useEffect(() => {
    return () => {
      if (autoSyncTimeoutRef.current) {
        clearTimeout(autoSyncTimeoutRef.current);
      }
    };
  }, []);

  return {
    personalizationOrigin,
    handleClientSelect,
    handleVariableUpdate,
  };
}
